/**
 * Returns the maintenance cost per mile based on total odometer miles.
 *
 * Tiers:
 *   0 – 50,000        → $0.04 / mile
 *   50,001 – 100,000  → $0.05 / mile
 *   100,001 – 125,000 → $0.09 / mile
 *   125,001 – 150,000 → $0.11 / mile
 *   150,001 – 175,000 → $0.13 / mile
 *   175,001 – 200,000 → $0.15 / mile
 *   … +$0.02 for every additional 25,000-mile increment beyond 100,000
 *
 * @param {number} totalMiles
 * @returns {number} cost per mile (e.g. 0.04)
 */
import { removeCommaAndRemoveCurrency } from "../services/number-service";

export function getMaintenanceCostPerMile(totalMiles) {
  const miles = Number(String(totalMiles || "").replace(/,/g, "")) || 0;

  switch (true) {
    case miles <= 50000:
      return 0.04;
    case miles <= 100000:
      return 0.05;
    default: {
      const incrementsAbove100k = Math.max(
        0,
        Math.floor((miles - 100001) / 25000)
      );
      return parseFloat((0.09 + incrementsAbove100k * 0.02).toFixed(4));
    }
  }
}

export function sumYearValues(replacementList) {
  let year1Value = 0;
  let year2Value = 0;
  let year3Value = 0;
  let year4Value = 0;
  let year5Value = 0;

  replacementList?.forEach((item) => {
    year1Value += Number(item["year1"] || 0);
    year2Value += Number(item["year2"] || 0);
    year3Value += Number(item["year3"] || 0);
    year4Value += Number(item["year4"] || 0);
    year5Value += Number(item["year5"] || 0);
  });

  return { year1Value, year2Value, year3Value, year4Value, year5Value };
}

export function sumEquityGains(newVehicle) {
  let equity1Gain = 0;
  let equity2Gain = 0;
  let equity3Gain = 0;
  let equity4Gain = 0;
  let equity5Gain = 0;

  newVehicle?.forEach((item) => {
    equity1Gain += Number(item["year1"] || 0);
    equity2Gain += Number(item["year2"] || 0);
    equity3Gain += Number(item["year3"] || 0);
    equity4Gain += Number(item["year4"] || 0);
    equity5Gain += Number(item["year5"] || 0);
  });

  return { equity1Gain, equity2Gain, equity3Gain, equity4Gain, equity5Gain };
}

export function sumSecondReplacementEquity(replacementList, newVehicle) {
  let equity1Gain = 0;
  let equity2Gain = 0;
  let equity3Gain = 0;
  let equity4Gain = 0;
  let equity5Gain = 0;

  replacementList?.forEach((fleet) => {
    const nv = newVehicle?.find((v) => v.fleetNo === fleet.fleetNo);
    if (!nv) return;

    // Requirement: Include only if Purchased/Financed > 0
    if (Number(nv.paymentAmount || 0) <= 0) return;

    const resaleValue = Number(nv.newEstimatedResaleValue || 0);
    const cost = Number(nv.newVehicleCost || 0);
    const residual = Number(nv.residualPercentage || 0) / 100;
    const gain = resaleValue - cost * residual;
    // Gain is 0 for Year 1 and Year 2
    if (nv.replaceYear1) {
      equity1Gain += 0;
    }

    if (nv.replaceYear2 && nv.replaceYear1) {
      equity2Gain += gain;
    }
    // Gain formula applied for Years 3, 4, and 5
    if (nv.replaceYear3 && (nv.replaceYear1 || nv.replaceYear2)) {
      equity3Gain += gain;
    }

    if (nv.replaceYear4 && (nv.replaceYear1 || nv.replaceYear2 || nv.replaceYear3)) {
      equity4Gain += gain;
    }

    if (nv.replaceYear5 && (nv.replaceYear1 || nv.replaceYear2 || nv.replaceYear3 || nv.replaceYear4) ) {
      equity5Gain += gain;
    }
  });

  return { equity1Gain, equity2Gain, equity3Gain, equity4Gain, equity5Gain };
}

export function sumMaintenanceCosts(maintanance) {
  let sumMaintenance = 0;
  let cycleYear1 = 0;
  let cycleYear2 = 0;
  let cycleYear3 = 0;
  let cycleYear4 = 0;
  let cycleYear5 = 0;

  maintanance?.forEach((item) => {
    sumMaintenance += Number(item["currentYearTotalCost"] || 0);
    cycleYear1 += Number(item["year_1_TotalCost"] || 0);
    cycleYear2 += Number(item["year_2_TotalCost"] || 0);
    cycleYear3 += Number(item["year_3_TotalCost"] || 0);
    cycleYear4 += Number(item["year_4_TotalCost"] || 0);
    cycleYear5 += Number(item["year_5_TotalCost"] || 0);
  });

  return {
    sumMaintenance,
    cycleYear1,
    cycleYear2,
    cycleYear3,
    cycleYear4,
    cycleYear5,
  };
}

export function sumFuelCosts(fuelListFilter) {
  let fuelYear1 = 0;
  let fuelYear2 = 0;
  let fuelYear3 = 0;
  let fuelYear4 = 0;
  let fuelYear5 = 0;

  fuelListFilter?.forEach((item) => {
    // Use annualFuelCost (actual) if available and non-zero, otherwise fall back to
    // anticipatedFuelCost (computed from MPG/mileage/price). This handles records
    // created before annualFuelCost was populated or where it wasn't entered.
    const y1 = removeCommaAndRemoveCurrency(item["annualFuelCostYear1"])
      || removeCommaAndRemoveCurrency(item["anticipatedFuelCostYear1"]);
    const y2 = removeCommaAndRemoveCurrency(item["annualFuelCostYear2"])
      || removeCommaAndRemoveCurrency(item["anticipatedFuelCostYear2"]);
    const y3 = removeCommaAndRemoveCurrency(item["annualFuelCostYear3"])
      || removeCommaAndRemoveCurrency(item["anticipatedFuelCostYear3"]);
    const y4 = removeCommaAndRemoveCurrency(item["annualFuelCostYear4"])
      || removeCommaAndRemoveCurrency(item["anticipatedFuelCostYear4"]);
    const y5 = removeCommaAndRemoveCurrency(item["annualFuelCostYear5"])
      || removeCommaAndRemoveCurrency(item["anticipatedFuelCostYear5"]);

    fuelYear1 += y1;
    fuelYear2 += y2;
    fuelYear3 += y3;
    fuelYear4 += y4;
    fuelYear5 += y5;
  });

  return { fuelYear1, fuelYear2, fuelYear3, fuelYear4, fuelYear5 };
}
